import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Target,
  Monitor,
  Layers,
  HeartHandshake,
  Calculator,
  Search,
  GitMerge,
  Users,
  ClipboardList,
  FileCheck
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Target,
      title: 'Management de projet et de programme',
      description: "Cadrage et pilotage de programmes stratégiques multi-axes complexes, incluant gouvernance, suivi d'avancement et gestion des risques.",
      details: [
        'Gouvernance de projets multi-flux',
        "Suivi d'avancement et reporting COMEX",
        'Gestion des risques',
        'Coordination des parties prenantes'
      ]
    },
    {
      icon: Monitor,
      title: "Déploiement de systèmes d'information",
      description: "Mise en œuvre complète d'outils de gestion métier, garantissant l'adéquation entre la solution technique et les besoins organisationnels.",
      details: [
        'Déploiement CRM (Salesforce)',
        'Intégration ERP',
        'Conduite du changement IT',
        'Formation utilisateurs'
      ]
    },
    {
      icon: Layers,
      title: 'Modèle opérationnel et conception des processus',
      description: "Conception du modèle opérationnel cible (TOM) et refonte des processus métier orientés efficacité et agilité.",
      details: [
        'Définition du Target Operating Model',
        'Refonte des processus métier',
        'Audit organisationnel',
        'Transformation agile'
      ]
    },
    {
      icon: HeartHandshake,
      title: 'Conduite du changement',
      description: "Accompagnement des organisations et des collaborateurs dans la traversée des périodes de transformation, via communication, formation et ateliers d'embarquement.",
      details: [
        "Animation d'ateliers d'embarquement",
        'Plans de communication',
        'Formation des équipes',
        'Séminaires de direction'
      ]
    },
    {
      icon: Calculator,
      title: 'Modèle économique et maîtrise des coûts',
      description: "Identification des leviers d'optimisation des coûts et construction d'une trajectoire durable d'amélioration de la performance financière.",
      details: [
        'Étude des leviers de réduction',
        'Plan de maîtrise des coûts',
        'Optimisation des ressources',
        'Trajectoire financière'
      ]
    },
    {
      icon: Search,
      title: "Sélection de solutions et assistance aux appels d'offres",
      description: "Pilotage des processus RFP/RFQ, définition des critères de sélection et garantie du choix du meilleur fournisseur ou de la meilleure solution.",
      details: [
        'Rédaction des documents RFP/RFQ',
        'Définition des critères de sélection',
        'Évaluation des prestataires',
        'Assistance à la décision'
      ]
    },
    {
      icon: GitMerge,
      title: 'Intégration post-fusion & acquisition',
      description: "Élaboration de la méthodologie d'intégration M&A et coordination du plan d'intégration sur les dimensions organisationnelle, IT et métier.",
      details: [
        "Méthodologie d'intégration M&A",
        "Plan d'intégration post-acquisition",
        'Coordination des dimensions',
        'Synergies opérationnelles'
      ]
    },
  ];

  const interventionModes = [
    {
      icon: Users,
      title: 'Management de projets IT et métier complexes',
      description: "Mise en place de la gouvernance de projets multi-flux en parallèle, incluant le suivi d'avancement, la gestion des risques et les mécanismes de reporting au top management."
    },
    {
      icon: ClipboardList,
      title: "Sélection de solutions et processus d'appel d'offres",
      description: "Pilotage de bout en bout du recueil des besoins, de la rédaction des documents RFP/RFQ, de la définition des critères de sélection et de l'évaluation des prestataires."
    },
    {
      icon: FileCheck,
      title: "Constitution et management d'équipes de chefs de projet",
      description: "Création, structuration et leadership au quotidien d'équipes de management de projet, en promouvant une méthodologie de travail unifiée."
    }
  ];

  const tools = [
    'Salesforce', 'Asana', 'Pack Office', 'Yooz'
  ];

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="caption-text text-[#BFA15F] mb-4">Services</p>
            <h1 className="heading-xl mb-6">
              Sept domaines<br />
              <span className="text-slate-400">d'intervention clés</span>
            </h1>
            <div className="divider mb-8" />
            <p className="body-large">
              Fort de près de 20 ans de pratique transversale et multi-sectorielle, 
              j'ai développé un socle de compétences couvrant l'ensemble de la chaîne 
              de valeur de la transformation organisationnelle.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="service-card group" data-testid={`service-card-${index}`}>
                <div className="service-card-icon">
                  <service.icon size={36} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-xl mb-4">{service.title}</h3>
                <p className="body-text mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start text-sm text-slate-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#BFA15F] mr-3 mt-2 flex-shrink-0" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intervention Modes */}
      <section data-testid="intervention-modes-section" className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="caption-text text-[#BFA15F] mb-4">Méthodologie</p>
            <h2 className="heading-lg">Modes d'intervention typiques</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {interventionModes.map((mode, index) => (
              <div key={index} className="bg-white p-8 border border-slate-100 hover:border-[#0a1628] transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-slate-50 text-[#0a1628] mb-6">
                  <mode.icon size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-4">{mode.title}</h3>
                <p className="body-text text-sm">{mode.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section data-testid="tools-section" className="py-16 bg-white border-y border-slate-100">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <p className="caption-text text-[#BFA15F] mb-2">Outils maîtrisés</p>
              <h3 className="font-heading font-semibold text-xl">Technologies et plateformes</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {tools.map((tool, index) => (
                <span key={index} className="px-4 py-2 bg-slate-50 text-slate-600 text-sm font-medium border border-slate-100">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0a1628] text-white">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6">
            Un besoin spécifique ?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-10">
            Chaque mission est unique. Discutons de vos enjeux pour construire 
            un accompagnement adapté à votre contexte.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:yoann.cohen@wyco.fr" 
              data-testid="services-contact-btn"
              className="btn-gold"
            >
              Contactez-moi
              <ArrowRight size={16} className="ml-2" />
            </a>
            <Link to="/seminaire" data-testid="services-seminaire-btn" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-[#0a1628]">
              Découvrir le séminaire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
